
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { supabase } from '@/integrations/supabase/client';
import PeriodsList from './components/PeriodsList';
import ScheduleSummary from './components/ScheduleSummary';
import TimeSlotsList from './components/TimeSlotsList';
import NoScheduleState from './components/NoScheduleState';
import EmptyDateState from './components/EmptyDateState';

interface Period {
  startTime: string;
  endTime: string;
}

interface Schedule {
  date: string;
  dayOfWeek: number;
  hasSchedule: boolean;
  periods: Period[];
  totalSlots: number;
}

interface DetailsPanelProps {
  selectedDate?: Date;
  schedule?: Schedule | null;
  onEditClick: () => void;
  onScheduleUpdate?: () => void;
  psychologistId?: string;
}

const DetailsPanel: React.FC<DetailsPanelProps> = ({
  selectedDate,
  schedule,
  onEditClick,
  onScheduleUpdate,
  psychologistId
}) => {
  console.log('🔍 [DetailsPanel] Renderizando com schedule:', schedule);

  const handleDeletePeriod = async (period: Period) => {
    console.log('🗑️ [DetailsPanel] Deletando período:', period);
    
    if (!schedule || !selectedDate || !psychologistId) {
      toast.error('Erro: dados necessários não encontrados');
      return;
    }

    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      const { error } = await supabase
        .from('psychologist_schedule')
        .delete()
        .eq('psychologist_id', psychologistId)
        .eq('date', dateStr)
        .eq('start_time', period.startTime)
        .eq('end_time', period.endTime);

      if (error) {
        console.error('❌ [DetailsPanel] Erro ao deletar do banco:', error);
        throw error;
      }

      console.log('✅ [DetailsPanel] Período deletado do banco com sucesso');

      if (onScheduleUpdate) {
        onScheduleUpdate();
      }

      toast.success(`Período ${period.startTime.substring(0, 5)} - ${period.endTime.substring(0, 5)} removido com sucesso!`);

    } catch (error) {
      console.error('❌ [DetailsPanel] Erro ao deletar período:', error);
      toast.error('Erro ao remover período: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
    }
  };

  if (!selectedDate) {
    return (
      <Card className="lg:col-span-2">
        <CardContent className="pt-6">
          <EmptyDateState />
        </CardContent>
      </Card>
    );
  }

  const dayName = format(selectedDate, "EEEE", { locale: ptBR });
  const formattedDate = format(selectedDate, "dd 'de' MMMM", { locale: ptBR });

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Horários para {dayName}
        </CardTitle>
        <p className="text-sm text-muted-foreground">{formattedDate}</p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {schedule?.hasSchedule && schedule.periods.length > 0 ? (
          <>
            <PeriodsList
              periods={schedule.periods}
              onEditClick={onEditClick}
              onDeletePeriod={handleDeletePeriod}
            />

            <ScheduleSummary
              periods={schedule.periods}
              totalSlots={schedule.totalSlots}
            />

            <TimeSlotsList periods={schedule.periods} />
          </>
        ) : (
          <NoScheduleState onEditClick={onEditClick} />
        )}
      </CardContent>
    </Card>
  );
};

export default DetailsPanel;
